import { useCallback, useEffect, useMemo, useState } from "react";
import { KpiSkeleton, TableSkeleton } from "../components/Skeletons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, GraduationCap, Loader2, Euro, ChevronRight, Clock,
  AlertTriangle, CheckCircle2, FileText, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const STATUS_META = {
  devis_envoye: { label: "Devis", color: "#f59e0b" },
  devis_signe: { label: "Devis signe", color: "#8b5cf6" },
  acompte_recu: { label: "Acompte", color: "#06b6d4" },
  inscrit: { label: "Inscrit", color: "#10b981" },
  en_cours: { label: "En cours", color: "#3b82f6" },
  termine: { label: "Termine", color: "#6b7280" },
  defaut: { label: "Defaut", color: "#ef4444" },
  annule: { label: "Annule", color: "#6b7280" },
  en_attente: { label: "En attente", color: "#f59e0b" },
};

export default function InscriptionsDashboardPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const api = axiosAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sessions"); // sessions | pipeline | clients
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/inscriptions/dashboard");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return { sessions: [], pipeline: [], clients: [] };
    const q = search.trim().toLowerCase();
    const m = (txt) => !q || (txt || "").toLowerCase().includes(q);
    return {
      sessions: (data.sessions || []).filter((s) => m(s.title) || m(s.program_title) || m(s.location)),
      pipeline: (data.pipeline || []).filter((p) => m(p.student_name) || m(p.student_email) || m(p.session_title) || m(p.devis_ref)),
      clients: (data.clients || []).filter((c) => m(c.student_name) || m(c.student_email) || m(c.student_company)),
    };
  }, [data, search]);

  if (loading || !data) return <div className="p-6 lg:p-10 space-y-6" data-testid="inscriptions-dashboard-loading"><KpiSkeleton count={4} /><TableSkeleton rows={6} cols={4} /></div>;

  const kpis = [
    { label: "Inscriptions totales", value: data.total_enrollments, icon: Users, color: "#f7c935" },
    { label: "Confirmes (siege)", value: data.confirmed_count, icon: CheckCircle2, color: "#10b981" },
    { label: "Dossiers a finaliser", value: data.pipeline_count, icon: Clock, color: "#f59e0b" },
    { label: "Sessions actives", value: (data.sessions || []).filter((s) => s.status === "en_cours" || s.status === "a_venir").length, icon: GraduationCap, color: "#3b82f6" },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="inscriptions-dashboard-page">
      <PageHeader
        icon={<GraduationCap />}
        title="Pilotage des inscriptions"
        description="Vue unifiée : places disponibles, dossiers à finaliser, profils stagiaires."
        breadcrumb={[{ label: "Inscriptions" }]}
        testId="inscriptions-header"
        actions={(
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#0b0811] border-white/10 text-white w-64" data-testid="insc-search" />
        )}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={"kpi-" + k.label.split(" ")[0].toLowerCase()}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</span>
                <Icon className="w-4 h-4" style={{ color: k.color }} />
              </div>
              <div className="text-3xl font-black text-white">{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Status donut (simple bar) */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-6">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Repartition des statuts</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.by_status || {}).sort((a, b) => b[1] - a[1]).map(([st, n]) => {
            const m = STATUS_META[st] || { label: st, color: "#64748b" };
            return (
              <div key={st} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0b0811] border" style={{ borderColor: m.color + "40" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: m.color }}></span>
                <span className="text-xs text-white font-semibold">{m.label}</span>
                <span className="text-xs text-slate-400">{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-white/10">
        {[
          { k: "sessions", l: "Sessions & places" },
          { k: "pipeline", l: "Dossiers a finaliser (" + (data.pipeline_count || 0) + ")" },
          { k: "clients", l: "Profils clients" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={"insc-tab-" + t.k}
            className={"px-4 py-2.5 text-xs font-bold border-b-2 -mb-px transition-colors " + (tab === t.k ? "border-[#f7c935] text-[#f7c935]" : "border-transparent text-slate-400 hover:text-white")}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "sessions" && <SessionsTable rows={filtered.sessions} navigate={navigate} />}
      {tab === "pipeline" && <PipelineTable rows={filtered.pipeline} navigate={navigate} api={api} reload={load} />}
      {tab === "clients" && <ClientsTable rows={filtered.clients} />}
    </div>
  );
}

function SessionsTable({ rows, navigate }) {
  if (!rows.length) return <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucune session.</div>;
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl overflow-hidden" data-testid="sessions-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5"><tr className="text-left text-[10px] uppercase text-slate-500">
          <th className="px-3 py-2">Session</th><th className="px-3 py-2">Dates</th><th className="px-3 py-2">Places</th><th className="px-3 py-2">Taux remplissage</th><th className="px-3 py-2">Statut</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.session_id} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => navigate("/admin/formation/sessions/" + r.session_id)} data-testid={"sess-row-" + r.session_id}>
              <td className="px-3 py-2">
                <div className="font-bold text-white">{r.title}</div>
                <div className="text-[10px] text-slate-500">{r.program_title} · {r.location || "-"}</div>
              </td>
              <td className="px-3 py-2 text-slate-300 text-xs">{r.start_date}<br /><span className="text-slate-500">→ {r.end_date}</span></td>
              <td className="px-3 py-2"><span className="font-bold text-white">{r.confirmed}</span><span className="text-slate-500"> / {r.max_participants}</span>{r.pipeline > 0 && <div className="text-[10px] text-amber-400">+{r.pipeline} en pipeline</div>}</td>
              <td className="px-3 py-2 w-40">
                <div className="bg-white/5 rounded-full h-1.5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.fill_rate_pct}%`, background: r.fill_rate_pct >= 80 ? "#ef4444" : r.fill_rate_pct >= 50 ? "#f7c935" : "#10b981" }} /></div>
                <div className="text-[10px] text-slate-500 mt-0.5">{r.fill_rate_pct}% · {r.free} places libres</div>
              </td>
              <td className="px-3 py-2"><span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: (r.status === "en_cours" ? "#10b981" : r.status === "a_venir" ? "#3b82f6" : "#6b7280") + "20", color: r.status === "en_cours" ? "#10b981" : r.status === "a_venir" ? "#3b82f6" : "#6b7280" }}>{r.status === "en_cours" ? "En cours" : r.status === "a_venir" ? "A venir" : "Terminee"}</span></td>
              <td className="px-3 py-2"><ChevronRight className="w-4 h-4 text-slate-600" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PipelineTable({ rows, navigate, api, reload }) {
  const updateStatus = async (enr, newStatus) => {
    try {
      await api.put("/lms/enrollments/" + enr.enrollment_id, { status: newStatus });
      toast.success("Statut mis a jour");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (!rows.length) return <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun dossier a finaliser.</div>;
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl overflow-hidden" data-testid="pipeline-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5"><tr className="text-left text-[10px] uppercase text-slate-500">
          <th className="px-3 py-2">Stagiaire</th><th className="px-3 py-2">Session</th><th className="px-3 py-2">Devis</th><th className="px-3 py-2">Paiement</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Actions</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => {
            const m = STATUS_META[r.status] || { label: r.status, color: "#64748b" };
            return (
              <tr key={r.enrollment_id} className="border-t border-white/5" data-testid={"pipeline-row-" + r.enrollment_id}>
                <td className="px-3 py-2">
                  <div className="font-bold text-white">{r.student_name}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{r.student_email}</div>
                  {r.student_company && <div className="text-[10px] text-slate-500">{r.student_company}</div>}
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => navigate("/admin/formation/sessions/" + r.session_id)} className="text-xs text-[#f7c935] hover:underline text-left">{r.session_title || "-"}</button>
                  <div className="text-[10px] text-slate-500">{r.session_start}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.devis_ref ? <a href="/facturation" className="text-purple-300 hover:underline">{r.devis_ref}</a> : <span className="text-slate-500">-</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-300">{r.payment_method || "-"}{r.financeur_name && <div className="text-[10px] text-slate-500">{r.financeur_name}</div>}</td>
                <td className="px-3 py-2"><span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: m.color + "20", color: m.color }} data-testid={"pipeline-status-" + r.enrollment_id}>{m.label}</span></td>
                <td className="px-3 py-2">
                  <select value={r.status} onChange={(e) => updateStatus(r, e.target.value)} className="bg-[#14101c] border border-white/10 text-white text-xs rounded px-1.5 py-1" data-testid={"pipeline-select-" + r.enrollment_id}>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClientsTable({ rows }) {
  if (!rows.length) return <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun client.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="clients-grid">
      {rows.map((c) => (
        <div key={c.student_email} className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="font-bold text-white">{c.student_name}</div>
          {c.student_company && <div className="text-xs text-slate-400 mt-0.5">{c.student_company}</div>}
          <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{c.student_email}</div>
          {c.student_phone && <div className="text-[10px] text-slate-500 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{c.student_phone}</div>}
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
            <span className="text-slate-500">{c.enrollments_count} inscription{c.enrollments_count > 1 ? "s" : ""} · {c.sessions.length} session{c.sessions.length > 1 ? "s" : ""}</span>
            <span className="text-[#f7c935] font-bold">{c.total_paid_ht.toFixed(0)} EUR</span>
          </div>
        </div>
      ))}
    </div>
  );
}
